<script lang="ts" setup>
import { SfLoaderCircular } from '@storefront-ui/vue';
import type { GetAddressesResponseItem } from '@vue-storefront/prestashop-api';

const { data: cart } = useCart();
const { query, mutationRemove, mutationSelect } = useAddresses();

const selectedAddress: Ref<GetAddressesResponseItem | null> = ref(null);
const isNew = ref(false);
const userAddresses = computed(() => query.data);
const isSelecting = ref(false);
const isDeleting = ref(false);
const selectingId = ref('');
const deletingId = ref('');

const selectAddress = async (address: GetAddressesResponseItem) => {
  selectingId.value = String(address.id);
  isSelecting.value = true;
  mutationSelect.mutate({
    id_address_invoice: address.id,
    id_address_delivery: address.id,
  });
  isSelecting.value = false;
};

const addAddress = () => {
  isNew.value = true;
  selectedAddress.value = null;
};

const editAddress = (address: GetAddressesResponseItem) => {
  selectedAddress.value = address;
};

const deleteAddressRequest = async (address: GetAddressesResponseItem) => {
  deletingId.value = String(address.id);
  isDeleting.value = true;
  mutationRemove.mutate(
    {
      id_address: address.id,
    },
    {
      onSuccess: () => {
        isDeleting.value = false;
      },
    },
  );
};

const onAddressList = () => {
  selectedAddress.value = null;
  isNew.value = false;
  window.scrollTo({
    behavior: 'smooth',
    top: 0,
  });
};
</script>

<template>
  <div class="addresses-list">
    <div class="grid grid-cols-1 @md:grid-cols-2 gap-8" v-if="!selectedAddress && isNew === false">
      <div
        class="relative cursor-pointer rounded-lg border bg-white p-4 w-full shadow-sm focus:outline-none"
        v-for="(address, index) in userAddresses"
        :key="index"
      >
        <UserShippingAddress v-bind="address" />
        <p
          v-if="cart && address.id === cart.id_address_delivery"
          class="flex items-center gap-1 text-sm text-gray-800 italic mt-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 text-gray-400">
            <path
              fill-rule="evenodd"
              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
              clip-rule="evenodd"
            />
          </svg>
          {{ $t('This is the default address') }}
        </p>
        <div v-else class="mt-2">
          <button class="btn btn-secondary" @click="selectAddress(address)" :disabled="isSelecting">
            <SfLoaderCircular v-if="selectingId == address.id && isSelecting" class="mr-2" />
            {{ $t('Select as default') }}
          </button>
        </div>
        <div class="flex gap-2 items-center mt-2">
          <button class="btn btn-secondary" @click="editAddress(address)">
            {{ $t('Edit') }}
          </button>
          <button class="btn btn-secondary" @click="deleteAddressRequest(address)" :disabled="isDeleting">
            <SfLoaderCircular v-if="deletingId == address.id && isDeleting" class="mr-2" />
            {{ $t('Delete') }}
          </button>
        </div>
      </div>
    </div>
    <AddressForm
      v-if="isNew || selectedAddress"
      type="shippingAddress"
      :saved-address="selectedAddress"
      @on-address-list="onAddressList"
    />
    <div v-else>
      <p v-if="userAddresses && Object.keys(userAddresses).length === 0">{{ $t('No addresses found') }}</p>
      <div class="mt-4">
        <button class="btn btn-secondary" @click="addAddress">
          {{ $t('Add new address') }}
        </button>
      </div>
    </div>
  </div>
</template>
